import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { api } from '@/lib/api'
import type { AdminJobSummary, AdminUserDetail, CreditLedgerEntry, Page } from '@/lib/types'
import { PageHeader } from '@/components/AppLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input, Textarea } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { formatDate, formatNumber } from '@/lib/utils'
import { ErrorNote } from './DashboardPage'
import { JobStatusBadge } from './JobsPage'

export function UserDetailPage() {
  const { userId = '' } = useParams()
  const queryClient = useQueryClient()

  const detail = useQuery({
    queryKey: ['user', userId],
    queryFn: () => api.get<AdminUserDetail>(`api/admin/users/${userId}`),
  })

  const ledger = useQuery({
    queryKey: ['user-ledger', userId],
    queryFn: () => api.get<Page<CreditLedgerEntry>>(`api/admin/users/${userId}/ledger`, { size: 25 }),
  })

  const generations = useQuery({
    queryKey: ['user-generations', userId],
    queryFn: () => api.get<Page<AdminJobSummary>>(`api/admin/users/${userId}/generations`, { size: 10 }),
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['user', userId] })
    void queryClient.invalidateQueries({ queryKey: ['user-ledger', userId] })
  }

  const setStatus = useMutation({
    mutationFn: ({ action, reason }: { action: 'disable' | 'enable'; reason?: string }) =>
      api.post(`api/admin/users/${userId}/${action}`, action === 'disable' ? { reason } : undefined),
    onSuccess: invalidate,
  })

  const adjustCredits = useMutation({
    mutationFn: (body: { amount: number; reason: string }) =>
      api.post(`api/admin/users/${userId}/credits`, body),
    onSuccess: invalidate,
  })

  if (detail.error) {
    return (
      <>
        <PageHeader title="User" />
        <div className="p-8">
          <ErrorNote message={(detail.error as Error).message} />
        </div>
      </>
    )
  }

  const user = detail.data

  return (
    <>
      <PageHeader
        title={user?.user.displayName ?? user?.user.email ?? 'User'}
        description={user?.user.subject}
        actions={
          user && (
            <>
              <AdjustCreditsDialog
                onSubmit={(amount, reason) => adjustCredits.mutate({ amount, reason })}
                pending={adjustCredits.isPending}
              />
              {user.user.status === 'ACTIVE' ? (
                <DisableDialog
                  onSubmit={(reason) => setStatus.mutate({ action: 'disable', reason })}
                  pending={setStatus.isPending}
                />
              ) : (
                <Button
                  variant="secondary"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ action: 'enable' })}
                >
                  Re-enable account
                </Button>
              )}
            </>
          )
        }
      />

      <div className="space-y-6 p-8">
        {(setStatus.error || adjustCredits.error) && (
          <ErrorNote message={((setStatus.error ?? adjustCredits.error) as Error).message} />
        )}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardHeader><CardTitle>Status</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Badge variant={user?.user.status === 'ACTIVE' ? 'success' : 'danger'}>
                {user?.user.status ?? '—'}
              </Badge>
              {user?.disabledReason && (
                <p className="text-xs text-[var(--color-text-tertiary)]">{user.disabledReason}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Subscription</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              <Badge variant={user?.subscription.pro ? 'accent' : 'default'}>
                {user?.subscription.pro ? 'Pro' : user?.subscription.status ?? 'None'}
              </Badge>
              {user?.subscription.currentPeriodEnd && (
                <p className="text-xs text-[var(--color-text-tertiary)]">
                  Until {formatDate(user.subscription.currentPeriodEnd)}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Credits</CardTitle></CardHeader>
            <CardContent>
              <p className="tabular text-2xl font-semibold">{formatNumber(user?.creditBalance)}</p>
              <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">
                {formatNumber(user?.lifetimeGranted)} granted · {formatNumber(user?.lifetimeSpent)} used
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Content</CardTitle></CardHeader>
            <CardContent>
              <p className="tabular text-2xl font-semibold">{formatNumber(user?.wallpaperCount)}</p>
              <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">
                {formatNumber(user?.generationCount)} generations
              </p>
            </CardContent>
          </Card>
        </section>

        <Card>
          <CardHeader><CardTitle>Interests</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {user?.interests.length
              ? user.interests.map((slug) => <Badge key={slug} variant="outline">{slug}</Badge>)
              : <span className="text-sm text-[var(--color-text-tertiary)]">None selected.</span>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Credit ledger</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Change</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledger.data?.items.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap text-[var(--color-text-secondary)]">
                      {formatDate(entry.createdAt)}
                    </TableCell>
                    <TableCell><Badge variant="outline">{entry.type}</Badge></TableCell>
                    <TableCell
                      className={`tabular font-medium ${
                        entry.delta > 0 ? 'text-[var(--color-success)]' : 'text-[var(--color-text-secondary)]'
                      }`}
                    >
                      {entry.delta > 0 ? '+' : ''}
                      {entry.delta}
                    </TableCell>
                    <TableCell className="tabular">{entry.balanceAfter}</TableCell>
                    <TableCell className="text-[var(--color-text-tertiary)]">{entry.note ?? '—'}</TableCell>
                  </TableRow>
                ))}
                {ledger.data?.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-[var(--color-text-tertiary)]">
                      No credit movements yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Recent generations</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Prompt</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {generations.data?.items.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="whitespace-nowrap text-[var(--color-text-secondary)]">
                      {formatDate(job.createdAt)}
                    </TableCell>
                    <TableCell><JobStatusBadge status={job.status} /></TableCell>
                    <TableCell className="max-w-xs truncate text-[var(--color-text-secondary)]">
                      {job.userPrompt ?? '—'}
                    </TableCell>
                    <TableCell className="text-[var(--color-text-tertiary)]">{job.errorCode ?? '—'}</TableCell>
                  </TableRow>
                ))}
                {generations.data?.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-[var(--color-text-tertiary)]">
                      No generations yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

/**
 * Credit adjustments require a reason, which is written to the audit log. A
 * balance change with no stated cause is not auditable, so the field is
 * mandatory rather than encouraged.
 */
function AdjustCreditsDialog({
  onSubmit,
  pending,
}: {
  onSubmit: (amount: number, reason: string) => void
  pending: boolean
}) {
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')

  const parsed = Number.parseInt(amount, 10)
  const valid = Number.isFinite(parsed) && parsed !== 0 && reason.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary">Adjust credits</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust credits</DialogTitle>
          <DialogDescription>
            Positive grants, negative deducts. This goes through the same ledger as everything else, so the
            user can see it and it cannot drive the balance below zero.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            type="number"
            placeholder="Amount, e.g. 10 or -5"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Textarea
            placeholder="Reason (recorded in the audit log)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || pending}
            onClick={() => {
              onSubmit(parsed, reason.trim())
              setOpen(false)
              setAmount('')
              setReason('')
            }}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DisableDialog({ onSubmit, pending }: { onSubmit: (reason: string) => void; pending: boolean }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive">Disable account</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disable this account?</DialogTitle>
          <DialogDescription>
            The user is signed out immediately and every API request is refused until the account is
            re-enabled. Their data is left untouched.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          placeholder="Reason (recorded in the audit log)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length === 0 || pending}
            onClick={() => {
              onSubmit(reason.trim())
              setOpen(false)
              setReason('')
            }}
          >
            Disable
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
